import * as dat from 'dat.gui';
import * as THREE from 'three';
import {
	OrbitControls
} from 'three/examples/jsm/controls/OrbitControls.js';
import {
	GLTFLoader
} from 'three/examples/jsm/loaders/GLTFLoader.js';

import VirtualScroll from 'virtual-scroll';


export default class Sketch {
	constructor(options) {
		this.wrap = options.dom;
		this.width = this.wrap.offsetWidth;
		this.height = this.wrap.offsetHeight;
		this.bgtrigger = false;
		this.booksReady = false;   // ⭐ 書本全載完才為 true
		this._enterDone = false;   // ⭐ #enterWrap 淡出完成才為 true
		this.mouse = new THREE.Vector2(0, 0);
		this.targetMouse = new THREE.Vector2(0, 0);

		this.imgs = [
			// 'images/history_01.png',
			// 'images/history_02.png',
			// 'images/history_03.png',
			// 'images/history_04.png',
			// 'images/history_05.png',
			// 'images/history_06.png',
			'images/history_01_20260716.png',
			'images/history_02_20260716.png',
			'images/history_03_20260716.png',
			'images/history_04_20260716.png',
			'images/history_05_20260716.png',
			'images/history_06_20260716.png',
			'images/history_07_20260716.png',
			'images/history_08_20260716.png',
			'images/history_09_20260716.png',
			'images/history_10_20260716.png',
			'images/history_11_20260716.png',
			'images/history_12_20260716.png',
			'images/history_13_20260716.png',
			'images/history_14_20260716.png',
			'images/history_15_20260716.png',
			'images/history_16_20260716.png',
			'images/history_17_20260716.png',
		];

		this.books = [
			{ glb: 'images/about-20260707.glb', url: 'about' },
			{ glb: 'images/news-20260707.glb', url: 'news' },
			{ glb: 'images/project-20260707.glb', url: 'latest' },
			{ glb: 'images/classic-20260707.glb', url: 'works' },
			{ glb: 'images/progress-20260707.glb', url: 'progress' },
		];

		this.scene = new THREE.Scene();

		// ⭐ 背景專用 scene & camera（不受 enterAnim 相機動態影響）
		this.bgScene = new THREE.Scene();
		this.bgCamera = new THREE.PerspectiveCamera(
			40,
			this.width / this.height,
			0.1,
			50
		);
		this.bgCamera.position.set(0, 0, 16);
		this.bgScene.add(this.bgCamera);

		this.camera = new THREE.PerspectiveCamera(
			20, // FOV 低 = 長焦平面感，無廣角變形
			this.width / this.height,
			0.1,
			50
		);
		// Z=15, FOV=40 的可視高度 ≈ 10.9，與 Z=60/FOV=40 的 43.7 比例一致
		// 只要把 targetHeight & spacing 同步縮成 1/4，視覺效果完全相同
		this.camera.position.set(0, 0, 0);
		this.scene.add(this.camera);

		this.renderer = new THREE.WebGLRenderer({
			alpha: true,
			antialias: true
		});
		this.renderer.setSize(this.width, this.height);
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping; // 讓光影過渡更自然
		this.renderer.toneMappingExposure = 1.2;
		this.renderer.outputEncoding = THREE.sRGBEncoding;
		this.renderer.physicallyCorrectLights = true;

		this.wrap.appendChild(this.renderer.domElement);

		// this.controls = new OrbitControls(this.camera, this.renderer.domElement);

		this.clock = new THREE.Clock();

		// ⭐ Raycaster 供 hover 偵測
		this.raycaster = new THREE.Raycaster();
		this.hoveredPivot = null;

		// ⭐ 點擊後鎖定互動
		this.isLocked = false;

		// ⭐ #index-nav li 快取，供 scroll 切換 current
		this.navItems = Array.from(document.querySelectorAll('#index-nav li'));
		this.activeNavIndex = -1;

		this.setMouseEvents();
		this.setLights();
		this.setGeometry();
		this.loadBooks();
		this.render();
		this.setResize();
		this._initEnterSequence(); // ⭐ 進場序列：影片 → fadeOut → enterAnim
	}

	// ⭐ 進場序列主控：影片播畢 → fadeOut → enterAnim
	_initEnterSequence() {
		const isMobile = (typeof device !== 'undefined' && device === 'mobile');
		// const $vid = $('#enter-video');
		const $vid = isMobile ? $('#enter-video-mobile') : $('#enter-video');
		const el = $vid.get(0);
		const enterWrap = document.getElementById('enterWrap');
		let entered = false;
		let playStarted = false;
		let fallbackTimer = null;
		let gestureBound = false;

		const safeEnter = () => {
			if (entered) return;
			entered = true;
			cleanup();
			// #enterWrap 淡出，完成後通知 Three.js
			$('#enterWrap').fadeOut(800, () => {
				this._enterDone = true;
				this._tryStartAnim();
			});
		};

		const clearFallbackTimer = () => {
			if (!fallbackTimer) return;
			clearTimeout(fallbackTimer);
			fallbackTimer = null;
		};

		const onEnded = () => {
			safeEnter();
		};

		const onPlaying = () => {
			playStarted = true;
			clearFallbackTimer();
		};

		const cleanup = () => {
			clearFallbackTimer();
			el.removeEventListener('loadedmetadata', onReady);
			el.removeEventListener('canplay', onReady);
			el.removeEventListener('playing', onPlaying);
			el.removeEventListener('ended', onEnded);
			if (enterWrap && gestureBound) {
				enterWrap.removeEventListener('pointerup', onUserGesture);
				gestureBound = false;
			}
		};

		const armGestureFallback = () => {
			if (!enterWrap || gestureBound || entered || playStarted) return;
			gestureBound = true;
			enterWrap.addEventListener('pointerup', onUserGesture, {
				once: true
			});
		};

		const attemptPlay = () => {
			if (entered || playStarted) return;
			const playPromise = el.play();
			if (playPromise !== undefined) {
				playPromise.catch(() => {
					// iOS 省電模式 / autoplay policy 常見會落到這裡，改交給使用者手勢補播。
					armGestureFallback();
				});
			}
		};

		function onReady() {
			attemptPlay();
		}

		function onUserGesture() {
			if (entered || playStarted) return;
			const playPromise = el.play();
			if (playPromise !== undefined) {
				playPromise.catch(() => {
					safeEnter();
				});
				return;
			}
			safeEnter();
		}

		if (!el) {
			// 找不到影片直接跳過
			safeEnter();
			return;
		}

		el.muted = true;
		el.playsInline = true;
		el.autoplay = true;

		// 不等 canplaythrough；metadata 一到就先播，避免手機網路稍慢就被 4 秒保險誤判。
		el.addEventListener('loadedmetadata', onReady);
		el.addEventListener('canplay', onReady);
		el.addEventListener('playing', onPlaying);
		el.addEventListener('ended', onEnded);

		// 真正卡住時才跳過；如果只是 autoplay 被擋，改用一次手勢補救。
		fallbackTimer = setTimeout(() => {
			if (playStarted || entered) return;
			if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
				attemptPlay();
				armGestureFallback();
				return;
			}
			console.log('video load timeout → skip');
			safeEnter();
		}, isMobile ? 8000 : 5000);

		el.load();
		attemptPlay();
	}

	// ⭐ 兩個條件都滿足（fadeOut 完 + 書本載完）才真正跑 enterAnim
	_tryStartAnim() {
		if (this._enterDone && this.booksReady) {
			this.enterAnim();
		}
		// 否則等另一個條件滿足時再呼叫一次（兩邊都會呼叫這個，先到的等後到的）
	}

	setMouseEvents() {
		window.addEventListener('mousemove', (e) => {
			// 將滑鼠座標正規化
			this.targetMouse.x = (e.clientX / this.width) * 2 - 1;
			this.targetMouse.y = (e.clientY / this.height) * 2 - 1;
		});
	}

	_shuffle(items) {
		const result = [...items];

		for (let i = result.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[result[i], result[j]] = [result[j], result[i]];
		}

		return result;
	}

	_refillXSlotBag() {
		const slots = Array.from({ length: this.xSlotCount }, (_, index) => index);
		this.xSlotBag = this._shuffle(slots);
	}

	_takeXSlot() {
		if (this.xSlotBag.length === 0) {
			this._refillXSlotBag();
		}

		return this.xSlotBag.pop();
	}

	_getRandomXFromSlot(visibleW) {
		const slotIndex = this._takeXSlot();
		return this._getXFromSlot(visibleW, slotIndex);
	}

	_getXFromSlot(visibleW, slotIndex) {
		const normalizedX = (slotIndex + 0.5) / this.xSlotCount - 0.5;
		const availableW = visibleW * this.xHorizontalSpread;
		const slotWidth = availableW / this.xSlotCount;
		const jitter = THREE.MathUtils.randFloatSpread(slotWidth * this.xJitterRange);

		return normalizedX * availableW + jitter;
	}

	setGeometry() {

		// ⭐ container 改掛到 bgScene
		this.container = new THREE.Object3D();
		this.bgScene.add(this.container);

		this.meshes = [];
		this.loader = new THREE.TextureLoader();

		const total = (device != 'mobile') ? 12 : 14;
		let imgscale = (device != 'mobile') ? 120 : 170;
		this.xSlotCount = (device != 'mobile') ? 12 : 6;
		this.xHorizontalSpread = 0.7;
		this.xJitterRange = 0.5;
		this.xSlotBag = [];
		const ySlotCount = total;
		const yRangeMin = -2.1;
		const yRangeMax = -0.5;
		const yJitterRange = 0.5;
		const selectedImgs = [];
		const selectedLeftXSlots = [];
		const selectedRightXSlots = [];
		const initialPlacements = [];

		while (selectedImgs.length < total) {
			const remaining = total - selectedImgs.length;
			selectedImgs.push(...this._shuffle(this.imgs).slice(0, remaining));
		}

		const sideSlotCount = this.xSlotCount / 2;
		const sideImageCount = total / 2;

		while (selectedLeftXSlots.length < sideImageCount) {
			const slots = Array.from({ length: sideSlotCount }, (_, index) => index);
			const remaining = sideImageCount - selectedLeftXSlots.length;
			selectedLeftXSlots.push(...this._shuffle(slots).slice(0, remaining));
		}

		while (selectedRightXSlots.length < sideImageCount) {
			const slots = Array.from(
				{ length: sideSlotCount },
				(_, index) => index + sideSlotCount
			);
			const remaining = sideImageCount - selectedRightXSlots.length;
			selectedRightXSlots.push(...this._shuffle(slots).slice(0, remaining));
		}

		const firstSideIsLeft = Math.random() < 0.5;
		let leftSlotIndex = 0;
		let rightSlotIndex = 0;

		for (let ySlotIndex = 0; ySlotIndex < ySlotCount; ySlotIndex++) {
			const isLeft = ySlotIndex % 2 === 0 ? firstSideIsLeft : !firstSideIsLeft;
			const xSlotIndex = isLeft
				? selectedLeftXSlots[leftSlotIndex++]
				: selectedRightXSlots[rightSlotIndex++];

			initialPlacements.push({ xSlotIndex, ySlotIndex });
		}

		for (let i = 0; i < total; i++) {

			this.loader.load(selectedImgs[i], (texture) => {
				const material = new THREE.MeshBasicMaterial({
					map: texture,
					transparent: true,
					depthWrite: false
				});

				// 初始 geometry 先隨便 1x1
				let geometry = new THREE.PlaneGeometry(1, 1);
				const mesh = new THREE.Mesh(geometry, material);

				// 儲存圖片尺寸
				mesh.size = {
					width: texture.image.width / imgscale, // 可以調整縮小比例
					height: texture.image.height / imgscale
				};

				// 用 resizeGeometry 調整 geometry
				this.resizeGeometry(mesh);

				// 隨機深度（遠近）
				mesh.position.z = THREE.MathUtils.randFloat(-9, 1);

				// 根據相機的 z 距離與 FOV 計算該深度下的可視寬度
				const distZ = this.bgCamera.position.z - mesh.position.z;
				const vFov = THREE.MathUtils.degToRad(this.bgCamera.fov);
				const visibleH = 2 * Math.tan(vFov / 2) * distZ;
				const visibleW = visibleH * this.bgCamera.aspect;
				const frontDistance = this.bgCamera.position.z - 1;
				mesh.depthSpeedFactor = distZ / frontDistance;
				const initialPlacement = initialPlacements[i];

				// 初始配置：每個 Y 欄位一張圖片，左右側依序交錯
				mesh.position.x = this._getXFromSlot(visibleW, initialPlacement.xSlotIndex);

				// 初始 Y 分段配置，並在各區段內加入小幅隨機偏移
				const ySlotSize = (yRangeMax - yRangeMin) / ySlotCount;
				const normalizedY = yRangeMin
					+ (initialPlacement.ySlotIndex + 0.5) * ySlotSize
					+ THREE.MathUtils.randFloatSpread(ySlotSize * yJitterRange);
				mesh.position.y = visibleH * normalizedY;

				// 儲存該 mesh 的可視範圍，供 update 使用
				mesh.visibleW = visibleW;
				mesh.visibleH = visibleH;

				// ⭐ 重要：儲存初始/基準位置，供視差計算參考
				mesh.baseX = mesh.position.x;
				mesh.baseY = mesh.position.y;

				mesh.material.opacity = 0;

				// 加到 container
				this.container.add(mesh);
				this.meshes.push(mesh);
			});
		}
		// ⭐ 黑色半透明遮罩，書本點擊時蓋在背景圖上
		const overlayGeo = new THREE.PlaneGeometry(200, 200);
		const overlayMat = new THREE.MeshBasicMaterial({
			color: 0x000000,
			transparent: true,
			opacity: 0,
			depthWrite: false,
		});
		this.blackOverlay = new THREE.Mesh(overlayGeo, overlayMat);
		this.blackOverlay.position.set(0, 0, -0.5); // 在圖片前一點
		this.blackOverlay.visible = false;
		this.bgScene.add(this.blackOverlay);
	}

	setLights() {
		// ⭐ environment 同步給兩個 scene
		const envTexture = new THREE.CubeTextureLoader()
			.setPath('https://threejs.org/examples/textures/cube/Bridge2/')
			.load([
				'posx.jpg', 'negx.jpg',
				'posy.jpg', 'negy.jpg',
				'posz.jpg', 'negz.jpg'
			]);

		this.scene.environment = envTexture;
		this.bgScene.environment = envTexture;

		// 對應 GLTF viewer: ambientIntensity=0.3, ambientColor=#ffffff
		const ambientLight = new THREE.AmbientLight(0xffffff, 1.3);
		this.scene.add(ambientLight);

		// 對應 GLTF viewer: directIntensity=2.5, directColor=#ffffff
		this.dirLight = new THREE.DirectionalLight(0xffffff, 2.5);
		this.dirLight.position.set(3, 5, 4);
		this.scene.add(this.dirLight);

		this.dirLightBot = new THREE.DirectionalLight(0xffffff, 2.5);
		this.dirLightBot.position.set(-3, -5, -4);
		this.scene.add(this.dirLightBot);

		// 🔦 Debug helper：視覺化方向光位置與照射方向（黃色箭頭）
		// const dirLightHelper = new THREE.DirectionalLightHelper(this.dirLight, 2);
		// this.scene.add(dirLightHelper);

		// const dirLightHelperBot = new THREE.DirectionalLightHelper(this.dirLightBot, 2);
		// this.scene.add(dirLightHelperBot);
	}

	loadBooks() {
		const gltfLoader = new GLTFLoader();
		const targetHeight = 1.2;
		const spacing = 3;
		const totalBooks = this.books.length;
		const startY = 0;

		// 所有書本的容器，Y 軸移動這個群組即可控制滾動
		this.booksGroup = new THREE.Object3D();
		this.scene.add(this.booksGroup);

		// 計算滾動範圍：從第一本到最後一本
		this.scrollMax = (totalBooks - 1) * spacing;

		// 初始化滾動狀態
		this.targetY = 0;
		this.currentY = 0;

		this.bookModels = []; // ← 重構時遺失，補回
		const loadedCount = {
			value: 0
		};

		this.books.forEach((book, index) => {
			const path = book.glb;
			const finalY = startY - index * spacing; // 最終停留的 Y

			gltfLoader.load(path, (gltf) => {
				const model = gltf.scene;

				const box = new THREE.Box3().setFromObject(model);
				const size = box.getSize(new THREE.Vector3());
				const center = box.getCenter(new THREE.Vector3());

				const scaleFactor = targetHeight / 2.5;
				model.scale.setScalar(scaleFactor);
				model.position.sub(center.multiplyScalar(scaleFactor));

				// ⭐ 三層巢狀：pivot（位置/縮放）→ tiltObj（斜角）→ spinWrapper（Y 自轉）
				const spinWrapper = new THREE.Object3D();
				spinWrapper.add(model);

				const tiltObj = new THREE.Object3D();
				tiltObj.add(spinWrapper);

				const pivot = new THREE.Object3D();
				pivot.add(tiltObj);

				// 進場前：起始位置在畫面上方外，翻轉 180°
				pivot.position.set(0, finalY + 13, 0); // 從上方飛入
				pivot.rotation.x = Math.PI / 3; // 背面朝外
				pivot._finalY = finalY; // 儲存目標 Y
				pivot._baseZ = 0;       // ⭐ 書本 Z 軸基準值
				pivot._url = book.url;  // ⭐ 對應換頁連結
				pivot._tiltObj = tiltObj;       // ⭐ 斜角控制
				pivot._spinWrapper = spinWrapper; // ⭐ Y 軸自轉控制

				// ⭐ 收集此 pivot 底下所有 mesh，供 hover 顏色控制
				pivot._meshes = [];
				const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();
				model.traverse((child) => {
					if (child.isMesh) {
						pivot._meshes.push(child);

						// ⭐ 對所有材質貼圖開啟最高各向異性過濾，防止靠近時模糊
						const mats = Array.isArray(child.material) ? child.material : [child.material];
						mats.forEach(mat => {
							['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'].forEach(mapKey => {
								const tex = mat[mapKey];
								if (tex) {
									tex.anisotropy = maxAnisotropy;
									tex.magFilter = THREE.LinearFilter;
									tex.minFilter = THREE.LinearMipmapLinearFilter;
									tex.needsUpdate = true;
								}
							});
						});
					}
				});

				this.booksGroup.add(pivot);
				this.bookModels.push(pivot);

				loadedCount.value++;
				console.log(`✅ ${path} | 載入 ${loadedCount.value}/${totalBooks}`);

				// 全部載完後設旗標，並嘗試啟動進場
				if (loadedCount.value == totalBooks) {
					this.booksReady = true;
					console.log('📚 書本全部載完');
					this._tryStartAnim();
				}
			}, (xhr) => {
				console.log(`📦 ${path}: ${(xhr.loaded / xhr.total * 100).toFixed(1)}%`);
			}, (err) => {
				console.error(`❌ Failed to load ${path}:`, err);
			});
		});
	}

	// ⭐ Hover 偵測與動畫
	setHover() {
		// 收集所有書本底下的 mesh，建立 mesh → pivot 的對照 map
		this.bookMeshToPivot = new Map();
		this.bookModels.forEach(pivot => {
			pivot._meshes.forEach(mesh => {
				this.bookMeshToPivot.set(mesh, pivot);
			});
		});

		// ⭐ 直接監聽 canvas，座標相對於 canvas 本身計算，避免偏移
		this.renderer.domElement.addEventListener('mousemove', (e) => {
			if (!this.bookMeshToPivot) return;
			if (this.isLocked) return; // ⭐ 鎖定時不處理 hover

			const rect = this.renderer.domElement.getBoundingClientRect();
			const canvasMouse = new THREE.Vector2(
				((e.clientX - rect.left) / rect.width) * 2 - 1,
				-((e.clientY - rect.top) / rect.height) * 2 + 1
			);

			// 用主相機做 raycasting
			this.raycaster.setFromCamera(canvasMouse, this.camera);

			const allBookMeshes = [...this.bookMeshToPivot.keys()];
			const intersects = this.raycaster.intersectObjects(allBookMeshes, false);

			// 找到命中的 pivot
			const hitPivot = intersects.length > 0
				? this.bookMeshToPivot.get(intersects[0].object)
				: null;

			// 若 hover 目標改變，才觸發動畫
			if (hitPivot !== this.hoveredPivot) {

				// 還原上一個 hover 的書
				if (this.hoveredPivot) {
					this._bookHoverOut(this.hoveredPivot);
				}

				// 進入新的 hover
				if (hitPivot) {
					this._bookHoverIn(hitPivot);
				}

				this.hoveredPivot = hitPivot;

				// 更新滑鼠樣式
				this.renderer.domElement.style.cursor = hitPivot ? 'pointer' : 'default';
			}
		});
	}

	_bookHoverIn(pivot) {
		// Z 往前 1.0
		gsap.to(pivot.position, {
			z: 1.5,
			duration: 0.4,
			ease: 'power2.out',
		});

		// 整體加深：把所有 mesh 的 color 乘上暗色（0.5 = 暗 50%）
		pivot._meshes.forEach(mesh => {
			if (mesh.material && mesh.material.color) {
				// 儲存原始顏色（只存一次）
				if (!mesh._originalColor) {
					mesh._originalColor = mesh.material.color.clone();
				}
				gsap.to(mesh.material.color, {
					r: mesh._originalColor.r * 0.5,
					g: mesh._originalColor.g * 0.5,
					b: mesh._originalColor.b * 0.5,
					duration: 0.4,
					ease: 'power2.out',
				});
			}
		});
	}

	_bookHoverOut(pivot) {
		// Z 恢復原位
		gsap.to(pivot.position, {
			z: 0,
			duration: 0.4,
			ease: 'power2.out',
		});

		// 顏色還原
		pivot._meshes.forEach(mesh => {
			if (mesh.material && mesh.material.color && mesh._originalColor) {
				gsap.to(mesh.material.color, {
					r: mesh._originalColor.r,
					g: mesh._originalColor.g,
					b: mesh._originalColor.b,
					duration: 0.4,
					ease: 'power2.out',
				});
			}
		});
	}

	// ⭐ 點擊動畫
	setClick() {
		this.clickedPivot = null;

		this.renderer.domElement.addEventListener('click', (e) => {
			if (!this.bookMeshToPivot) return;
			if (this.isLocked) return; // ⭐ 鎖定中不接受任何點擊

			const rect = this.renderer.domElement.getBoundingClientRect();
			const canvasMouse = new THREE.Vector2(
				((e.clientX - rect.left) / rect.width) * 2 - 1,
				-((e.clientY - rect.top) / rect.height) * 2 + 1
			);

			this.raycaster.setFromCamera(canvasMouse, this.camera);
			const allBookMeshes = [...this.bookMeshToPivot.keys()];
			const intersects = this.raycaster.intersectObjects(allBookMeshes, false);

			const hitPivot = intersects.length > 0
				? this.bookMeshToPivot.get(intersects[0].object)
				: null;

			// 點擊空白處：還原所有書
			if (!hitPivot) {
				if (this.clickedPivot) {
					this._bookClickOut();
				}
				return;
			}

			// 點擊同一本：還原
			if (hitPivot === this.clickedPivot) {
				this._bookClickOut();
				return;
			}

			// 若之前有選中的書，先還原
			if (this.clickedPivot) {
				this._bookClickOut();
			}

			// 進入點擊狀態
			this.clickedPivot = hitPivot;
			this._bookClickIn(hitPivot);
		});
	}

	_bookClickIn(activePivot) {
		$("#ryderStage").addClass("is-animating");

		const tilt = activePivot._tiltObj;
		const spin = activePivot._spinWrapper;

		// ⭐ 立即鎖定：禁止 hover 與 scroll
		this.isLocked = true;
		this.renderer.domElement.style.cursor = 'default';

		// ⭐ 強制清除當前 hover 狀態
		if (this.hoveredPivot) {
			this._bookHoverOut(this.hoveredPivot);
			this.hoveredPivot = null;
		}

		// ⭐ GSAP tween scroll 到該書置中（booksGroup.position.y = -finalY）
		// booksGroup 往上移 = currentY 增加，讓書出現在畫面中央
		const targetScrollY = -activePivot._finalY;
		gsap.to(this, {
			targetY: targetScrollY,
			duration: 1,
			ease: 'power2.inOut',
		});

		// Step 1：tiltObj 旋轉到斜角（X: 0.3π, Y: -0.3π）
		// Step 2：斜角完成後，spinWrapper 繞自身 Y 軸轉一圈（+2π）停住
		const tl = gsap.timeline({
			onComplete: () => {
				this.isLocked = false;
			}
		});

		tl.to(tilt.rotation, {
			x: Math.PI * 0.2,
			y: -Math.PI * 0.35,
			duration: 1.5,
		}).to(tilt.rotation, {
			x: Math.PI * 0.5,
			y: -Math.PI * 0.46,
			duration: 2.5,
			ease: 'circle.inOut',
		})

		tl.to(spin.rotation, {
			x: Math.PI * 2.1,
			duration: 4.5,
			ease: 'power1.out',
		}, 1.5)

		tl.to(activePivot.position, {
			z: 22,
			duration: 6,
			ease: 'power2.inOut',
		}, 0)

		// ⭐ 背景遮罩 fade in
		if (this.blackOverlay) {
			this.blackOverlay.visible = true;
			gsap.to(this.blackOverlay.material, {
				opacity: 0.6,
				duration: 1.5,
			});
		}

		// 其餘的書：z 軸往後退出
		this.bookModels.forEach(pivot => {
			if (pivot === activePivot) return;

			gsap.to(pivot.position, {
				z: -40,
				duration: 1,
				ease: 'power2.inOut',
			});
		});

		// 換頁
		tl.to("#coverbox", {
			opacity: 1,
			duration: 2,
			onComplete: () => {
				location.href = activePivot._url;
			}
		}, ">-2")
	}

	_bookClickOut() {
		$("#ryderStage").removeClass("is-animating");

		const tilt = this.clickedPivot._tiltObj;
		const spin = this.clickedPivot._spinWrapper;

		// ⭐ 解鎖互動
		this.isLocked = false;

		// 點擊的書：斜角歸零、Z 歸位
		if (this.clickedPivot) {
			gsap.to(this.clickedPivot.position, {
				z: 0,
				duration: 1,
			});
			// tiltObj 斜角歸零
			gsap.to(tilt.rotation, {
				x: 0,
				y: 0,
				duration: 1,
			});
			gsap.to(spin.rotation, {
				x: 0,
				y: 0,
				duration: 1,
			});
		}

		// ⭐ 背景遮罩 fade out
		if (this.blackOverlay) {
			gsap.to(this.blackOverlay.material, {
				opacity: 0,
				duration: 0.6,
				ease: 'power2.out',
				onComplete: () => {
					this.blackOverlay.visible = false;
				},
			});
		}

		// 所有其他書：z 軸歸位
		this.bookModels.forEach(pivot => {
			if (pivot === this.clickedPivot) return;

			gsap.to(pivot.position, {
				z: 0,
				duration: 1,
			});
		});

		this.clickedPivot = null;
	}

	enterAnim() {
		const sorted = [...this.bookModels].sort((a, b) => b._finalY - a._finalY);
		const sortedtotal = sorted.length;

		gsap.to("#coverbox", {
			opacity: 0,
			duration: 0.3,
		})

		// 🎥 相機初始：位置不變，但向下傾斜約 34°
		gsap.set(this.camera.position, {
			z: 35
		}); // 確保 Z 在正確位置
		gsap.set(this.camera.rotation, {
			x: -0.25
		}); // 正值 = 向下傾斜（~34°）

		const tl = gsap.timeline({
			onComplete: () => {
				this.setScroll();
				this.setHover(); // ⭐ 全部載完後才設定 hover
				this.setClick(); // ⭐ 點擊動畫
				console.log('🏙️ Intro 完成，開放滾動');
			}
		});

		// 書本依序落下到定位，相機保持向下傾斜
		sorted.forEach((pivot, i) => {
			tl.to(pivot.position, {
				y: pivot._finalY,
				duration: 4,
				ease: 'power2.inOut',
			}, (sortedtotal - i) * 0.3); // 每本依序延遲

			tl.to(pivot.rotation, {
				x: 0,
				delay: 1,
				duration: 4,
				ease: 'power2.inOut',
			}, (sortedtotal - i) * 0.3);
		});

		// 相機同步向上回正
		tl.to(this.camera.rotation, {
			x: 0,
			duration: 2.5,
			ease: 'power2.inOut',
			onStart: () => {
				this.startBg();
			}
		}, 4.5);

		tl.to(this.camera.position, {
			z: 30,
			duration: 2.5,
			ease: 'power2.inOut',
		}, 4.5);


		gsap.from("#index-nav li", {
			delay: 4,
			duration: 1,
			scaleX: 0,
			stagger: {
				each: .234,
			},
		})

		gsap.from("#index-logo", {
			delay: 6.5,
			duration: 1.2,
			y: -200,
			opacity: 0,
		})

		gsap.from("#index-contact", {
			delay: 6.5,
			duration: 1.5,
			yPercent: 100,
			opacity: 0,
		})
	}

	startBg() {
		this.bgtrigger = true;

		this.meshes.forEach(mesh => {
			// 透明度設定
			// mesh.material.opacity = 0.4;
			mesh.material.opacity = 0.6;
		});
	}

	setScroll() {
		const vs = new VirtualScroll();
		const scrollSpeed = device === 'mobile' ? 0.016 : 0.003;

		vs.on((event) => {
			if (this.isLocked) return; // ⭐ 點擊後禁止 scroll

			// deltaY 滞下為負，對應 booksGroup 往下移動（看下一本）
			this.targetY -= event.deltaY * scrollSpeed;

			// 限制範圍：0 = 第一本，scrollMax = 最後一本
			this.targetY = Math.max(0, Math.min(this.scrollMax, this.targetY));
		});
	}

	// ✅ resizeGeometry 函式
	resizeGeometry(mesh) {
		mesh.geometry.dispose(); // 釋放舊 geometry
		mesh.geometry = new THREE.PlaneBufferGeometry(mesh.size.width, mesh.size.height);
	}

	update() {
		const delta = Math.min(this.clock.getDelta(), 0.05);

		// 1. 滑鼠數值平滑化 (Lerp 讓移動更有質感)
		this.mouse.x = THREE.MathUtils.lerp(this.mouse.x, this.targetMouse.x, 0.05);
		this.mouse.y = THREE.MathUtils.lerp(this.mouse.y, this.targetMouse.y, 0.05);

		// 2. 書本滾動 (維持不變)
		if (this.booksGroup) {
			this.currentY = THREE.MathUtils.lerp(this.currentY, this.targetY, 0.08);
			this.booksGroup.position.y = this.currentY;

			// ⭐ 根據 currentY 切換 #index-nav 的 current class
			// spacing = 3，currentY 即為已滾動量，四捨五入得當前書本 index
			if (this.navItems.length > 0) {
				const spacing = 3;
				const rawIndex = this.currentY / spacing;
				const newIndex = Math.round(rawIndex);
				const clampedIndex = Math.max(0, Math.min(this.navItems.length - 1, newIndex));

				// 只在 index 有變化時更新 DOM，避免每 frame 操作
				if (clampedIndex !== this.activeNavIndex) {
					if (this.activeNavIndex >= 0 && this.navItems[this.activeNavIndex]) {
						this.navItems[this.activeNavIndex].classList.remove('current');
					}
					this.navItems[clampedIndex].classList.add('current');
					this.activeNavIndex = clampedIndex;
				}
			}
		}

		// 3. 背景圖片：向上飄動 + 全方位視差
		if (this.bgtrigger) {
			this.meshes.forEach(mesh => {
				// A. 更新基準 Y (持續向上漂浮)
				mesh.baseY += 0.6 * delta * mesh.depthSpeedFactor;

				// B. 計算視差強度 (深度越深，位移量越大)
				// 因為 Z 是負值 (-10 ~ -1)，我們取正值來當係數
				const parallaxPower = -mesh.position.z * 0.15;

				// C. 計算最終位置 = 基準位置 - (滑鼠位移 * 強度)
				// 使用減法達成「反向」效果
				mesh.position.x = mesh.baseX - (this.mouse.x * parallaxPower);
				// mesh.position.y = mesh.baseY + (this.mouse.y * parallaxPower); // 註：滑鼠往下 Y 是正，圖片往上加會變反向
				mesh.position.y = mesh.baseY;

				// D. 邊界檢查 (使用 baseY 判斷)
				const topLimit = mesh.visibleH ? mesh.visibleH * 0.7 : 12;
				const botLimit = mesh.visibleH ? -mesh.visibleH * 0.7 : -12;

				if (mesh.baseY > topLimit) {
					mesh.baseY = botLimit;
					mesh.baseX = this._getRandomXFromSlot(mesh.visibleW);
				}
			});
		}
	}

	render() {
		this.update();

		// ⭐ 先渲染背景 scene
		this.renderer.autoClear = true;
		this.renderer.render(this.bgScene, this.bgCamera);

		// ⭐ 再疊加渲染主 scene（保留背景不清空）
		this.renderer.autoClear = false;
		this.renderer.render(this.scene, this.camera);

		// ⭐ 恢復預設
		this.renderer.autoClear = true;

		requestAnimationFrame(this.render.bind(this));
	}

	setResize() {
		window.addEventListener('resize', this.onResize.bind(this));
	}

	onResize() {
		this.width = this.wrap.offsetWidth;
		this.height = this.wrap.offsetHeight;

		this.camera.aspect = this.width / this.height;
		this.camera.updateProjectionMatrix();

		// ⭐ bgCamera 同步更新
		this.bgCamera.aspect = this.width / this.height;
		this.bgCamera.updateProjectionMatrix();

		this.renderer.setSize(this.width, this.height);
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
	}
}


const $stage = document.querySelector('#ryderStage')

if ($stage) {
	new Sketch({
		dom: $stage
	});
}
